// src/App.js
import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import ThemeProviderWrapper from "./ThemeProviderWrapper";
import { themes } from "./themes";
import { ResponsiveProvider } from "./contexts/ResponsiveContext";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AuthForm from "./components/AuthForm";
import ConfirmSignUp from "./components/ConfirmSignUp";
import ProtectedRoute from "./components/ProtectedRoute";
import Loading from "./components/Loading";
import {
  clearAuthState,
  isAuthenticated as checkAuth,
  isTokenExpired,
} from "./utils/authUtils";
import {
  preventAWSRedirects,
  clearAllAWSState,
} from "./utils/awsRedirectPrevention";
import AuthGuard from "./components/AuthGuard";
import ThemeBootstrapper from "./components/ThemeBootstrapper";
import CssBaseline from "@mui/material/CssBaseline";

// Lazy-loaded components
const Dashboard = lazy(() => import("./components/Dashboard"));
const SubscriptionPlansPage = lazy(
  () => import("./components/SubscriptionPlansPage"),
);
const Startup = lazy(() => import("./components/Startup"));
const BrandManagementStudio = lazy(
  () => import("./components/brand-kit/BrandManagementStudio"),
);
const AssetsStudioPageV2 = lazy(
  () => import("./components/AssetsStudioPageV2"),
);
const ContentFeedPage = lazy(() => import("./components/ContentFeedPage"));
const SubscriptionSuccessPage = lazy(
  () => import("./components/SubscriptionSuccessPage"),
);
const ReporterAgentPage = lazy(() => import("./components/ReporterAgentPage"));
const ResearchDeckPage = lazy(() => import("./components/ResearchDeckPage"));
const PlaywrightRegressionPage = lazy(() => import("./components/PlaywrightRegressionPage"));
const DiagramsAgentPage = lazy(() => import("./components/DiagramsAgentPage"));
const DiagramsReposManagePage = lazy(() => import("./components/DiagramsReposManagePage"));
const HelpPage = lazy(() => import("./components/HelpPage"));
const HelpArticlePage = lazy(() => import("./components/help/HelpArticlePage"));
const FeaturePage = lazy(() => import("./components/features/FeaturePage"));
const GetStartedPage = lazy(() => import("./components/GetStartedPage"));

// Component to prevent accidental swipe-back navigation (Safari's white circle arrow gesture)
// eslint-disable-next-line no-unused-vars -- intentionally not rendered (disabled); see note at former usage site
const SwipeNavigationBlocker = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPathRef = React.useRef(location.pathname);
  const isBlockingRef = React.useRef(false);

  useEffect(() => {
    // Update ref when location changes
    currentPathRef.current = location.pathname;
    isBlockingRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // List of public/auth pages where back navigation is allowed
    const publicPages = ["/login", "/register", "/confirm-signup"];
    const isOnPublicPage = publicPages.some(
      (page) => location.pathname === page,
    );

    // Only block navigation on protected routes
    if (isOnPublicPage) return;

    const handlePopState = (event) => {
      // Prevent multiple rapid triggers
      if (isBlockingRef.current) return;

      const targetPath = currentPathRef.current;
      const currentUrl = window.location.pathname;

      // If we're navigating away from our target path, block it
      if (currentUrl !== targetPath) {
        isBlockingRef.current = true;

        // Immediately replace the URL to prevent navigation
        window.history.replaceState(null, "", targetPath);

        // Use React Router's navigate to ensure the component updates
        navigate(targetPath, { replace: true });

        console.log(
          "Prevented accidental back navigation from Safari swipe gesture",
        );

        // Reset blocking flag after a short delay
        setTimeout(() => {
          isBlockingRef.current = false;
        }, 100);
      }
    };

    // Push current state to history stack to detect back navigation
    // This creates a history entry that Safari's swipe gesture can navigate back to
    window.history.pushState({ preventBack: true }, "", currentPathRef.current);

    // Listen for popstate events (triggered by browser back/forward buttons or Safari swipe gestures)
    window.addEventListener("popstate", handlePopState, { passive: true });

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isAuthenticated, location.pathname, navigate]);

  return null;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Lazy initializer — reads THEME from localStorage synchronously on first render.
  // If no saved theme exists, returns the in-memory default WITHOUT writing to localStorage.
  // ThemeBootstrapper will write the correct DDB theme after login.
  const [theme, setTheme] = useState(() => {
    try {
      // Only honor a saved personal theme for AUTHENTICATED users; unauth visitors
      // get the brand default (refined by the no-store /site-theme.json fetch) so a
      // stale localStorage theme can never flash on first paint.
      const token = localStorage.getItem("jwtToken");
      const isAuthed = token && !isTokenExpired(token);
      if (isAuthed) {
        const savedTheme = localStorage.getItem("THEME");
        if (savedTheme) return JSON.parse(savedTheme);
      }
    } catch (e) {
      console.error("App.js - Error parsing saved theme:", e);
    }
    return themes.midnight;
  });

  // Fetch the site owner's selected theme unconditionally on startup.
  // This makes unauthenticated visitors (prospective clients, CTA click-throughs)
  // see the same brand as the authenticated owner — not the hardcoded default.
  useEffect(() => {
    fetch("/site-theme.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((t) => {
        if (t && t.primaryColor) handleThemeChange(t);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log("App.js - Checking authentication state");

    // Start AWS redirect prevention
    const cleanup = preventAWSRedirects();

    // Restore session if a valid (non-expired) token already exists.
    // Opening a new tab must NOT wipe the token from the shared localStorage
    // and log the user out of other tabs.
    const existingToken = localStorage.getItem("jwtToken");
    if (existingToken && !isTokenExpired(existingToken)) {
      console.log(
        "App.js - Valid session found, restoring authenticated state",
      );
      setIsAuthenticated(true);
    } else {
      // No valid session — start fresh
      clearAllAWSState();
      clearAuthState();
      setIsAuthenticated(false);
    }

    // Return cleanup function
    return cleanup;
  }, []);

  // Token expiration and inactivity timeout monitoring
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check token expiration and inactivity every 30 seconds
    const checkInterval = setInterval(() => {
      const token = localStorage.getItem("jwtToken");

      // Check if token is expired
      if (token && isTokenExpired(token)) {
        console.warn("App.js - Token expired, logging out user");
        clearAuthState();
        setIsAuthenticated(false);
        // Force navigation to login and clear any route state
        window.location.replace("/login");
        return;
      }

      // Check inactivity timeout (30 minutes)
      const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
      const lastActivityTime = localStorage.getItem("lastActivityTime");

      if (lastActivityTime) {
        const timeSinceLastActivity =
          Date.now() - parseInt(lastActivityTime, 10);

        if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
          console.warn("App.js - Inactivity timeout reached, logging out user");
          clearAuthState();
          setIsAuthenticated(false);
          // Force navigation to login and clear any route state
          window.location.replace("/login");
          return;
        }
      }
    }, 30000); // Check every 30 seconds

    // Track user activity (mouse movements, clicks, keyboard)
    const updateActivityTime = () => {
      if (isAuthenticated) {
        localStorage.setItem("lastActivityTime", Date.now().toString());
      }
    };

    // Add event listeners for user activity
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
    events.forEach((event) => {
      window.addEventListener(event, updateActivityTime, { passive: true });
    });

    return () => {
      clearInterval(checkInterval);
      events.forEach((event) => {
        window.removeEventListener(event, updateActivityTime);
      });
    };
  }, [isAuthenticated]);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("THEME", JSON.stringify(newTheme));
  };

  return (
    <Router>
      <ResponsiveProvider>
        <ThemeProviderWrapper theme={theme}>
          <DndProvider backend={HTML5Backend}>
            <CssBaseline />
            <ThemeBootstrapper
              isAuthenticated={isAuthenticated}
              onThemeLoaded={handleThemeChange}
            />
            {/* SwipeNavigationBlocker disabled: it blocked legitimate browser back-navigation and caused blank-page-on-back via bfcache. */}
            <AuthGuard>
              <Header
                isAuthenticated={isAuthenticated}
                setIsAuthenticated={setIsAuthenticated}
              />
              <Suspense fallback={<Loading />}>
                <Routes>
                  {/* Public routes - accessible without authentication */}
                  <Route path="/" element={<Dashboard />} />
                  <Route
                    path="/subscription-plans"
                    element={
                      <SubscriptionPlansPage
                        isAuthenticated={isAuthenticated}
                      />
                    }
                  />
                  <Route
                    path="/subscription-success"
                    element={<SubscriptionSuccessPage />}
                  />
                  <Route
                    path="/login"
                    element={
                      <AuthForm setIsAuthenticated={setIsAuthenticated} />
                    }
                  />
                  <Route
                    path="/register"
                    element={
                      <AuthForm setIsAuthenticated={setIsAuthenticated} />
                    }
                  />
                  <Route path="/confirm-signup" element={<ConfirmSignUp />} />
                  <Route path="/get-started" element={<GetStartedPage />} />
                  <Route path="/features/:slug" element={<FeaturePage />} />

                  {/* All other routes - require authentication or redirect to login */}
                  {isAuthenticated ? (
                    <>
                      {/* Protected routes - require authentication */}
                      <Route
                        path="/brand-management-studio"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <BrandManagementStudio
                              onChangeTheme={handleThemeChange}
                            />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/startup"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <Startup />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/assets-studio-v2"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <AssetsStudioPageV2 />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/content-feed"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <ContentFeedPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/reporter-agent"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <ReporterAgentPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/research-deck"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <ResearchDeckPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/regression-snapshots"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <PlaywrightRegressionPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/architecture-diagrams"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <DiagramsAgentPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/architecture-diagrams/repos"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <DiagramsReposManagePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/help"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <HelpPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/help/:slug"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <HelpArticlePage />
                          </ProtectedRoute>
                        }
                      />
                    </>
                  ) : (
                    /* If not authenticated, redirect ALL routes to login */
                    <Route
                      path="*"
                      element={<Navigate to="/login" replace />}
                    />
                  )}
                </Routes>
              </Suspense>
              <Footer />
            </AuthGuard>
          </DndProvider>
        </ThemeProviderWrapper>
      </ResponsiveProvider>
    </Router>
  );
};

export default App;
